import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { appServicesAPI, clustersAPI } from '../../services/api';
import { useCluster } from '../../context/ClusterContext';
import ActionsMenu, { MENU_ITEM } from '../../components/ActionsMenu';

/**
 * Every K8s Service SarvKube created alongside a tracked workload (My Workloads) —
 * requested live: "services should show differently" from the Deployments/StatefulSets
 * they belong to, instead of just a Port/Type column buried in that table.
 *
 * Not a new data source: each AppService row already carries port/service_type (the
 * values used to create its Service) — this page is a different VIEW over the same
 * app_service rows, filtered to ones that actually have a Service (port is set), not a
 * separate table or endpoint.
 *
 * No live Cluster IP column: confirmed live that the bulk list endpoint's reconcile path
 * (app_services.py, the generic-agent branch) only ever writes k8s_status={"pods": [...]}
 * — it never populates a "service" key with the real cluster_ip, unlike the richer
 * describe-style function used elsewhere (the single-workload detail drawer). Showing a
 * column that can never populate through this fetch path would just be a permanent "—".
 * Type/Port below are the DB-stored values from when the Service was created — accurate,
 * just not re-verified against the live cluster on every load.
 */
function Age({ ts }) {
  if (!ts) return <span style={{ color:'var(--muted-2)' }}>—</span>;
  const s = Math.floor((Date.now()-new Date(ts).getTime())/1000);
  if (s<60) return <span>{s}s</span>;
  if (s<3600) return <span>{Math.floor(s/60)}m</span>;
  if (s<86400) return <span>{Math.floor(s/3600)}h</span>;
  return <span>{Math.floor(s/86400)}d</span>;
}

export default function MyServicesPage() {
  const { selectedCluster } = useCluster();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');

  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    const cid = selectedCluster?.id;
    setLoading(true);
    try {
      const [sR, cR] = await Promise.all([
        appServicesAPI.list(cid, false),   // DB rows only — no live Service data to wait on (see module docstring)
        clustersAPI.list(),
      ]);
      if (seq !== loadSeq.current) return;   // a cluster switch mid-load discards this
      setClusters(cR.data || []);
      // Only rows that actually HAVE a Service — svc.port is what create() used to make
      // one; a bare Pod or a raw-YAML deploy with no port never got one.
      setServices((sR.data || []).filter(s => s.port));
    } catch (e) {}
    finally { if (seq === loadSeq.current) setLoading(false); }
  }, [selectedCluster]);

  useEffect(() => { load(); }, [load]);

  const clusterName = id => clusters.find(c => c.id === id)?.name || `cluster-${id}`;
  const filtered = services.filter(s =>
    !filter || s.name.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div style={{ padding:'24px 32px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h1 style={{ margin:0, fontSize:22, fontWeight:700, color:'var(--ink)' }}>My Services</h1>
          <p style={{ margin:'4px 0 0', fontSize:13, color:'var(--muted)' }}>
            The K8s Service SarvKube created for each workload in My Workloads — not every
            Service on the cluster. To browse <em>everything</em>, use Network → Services in
            the sidebar.
          </p>
        </div>
        <button onClick={load} disabled={loading} className="btn btn-secondary btn-sm">
          {loading ? '…' : '⟳ Refresh'}
        </button>
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:16 }}>
        <input placeholder="Search by name…" value={filter}
          onChange={e => setFilter(e.target.value)}
          className="input" style={{ width:260 }} />
        <span style={{ marginLeft:'auto', fontSize:12, color:'var(--muted-2)', alignSelf:'center' }}>
          {filtered.length} / {services.length} services
        </span>
      </div>

      {!loading && services.length === 0 ? (
        <div className="card" style={{ textAlign:'center', padding:'80px 20px' }}>
          <div style={{ fontSize:48, marginBottom:12 }}>🔌</div>
          <div style={{ fontSize:16, fontWeight:600, color:'var(--ink-2)', marginBottom:8 }}>
            No services yet
          </div>
          <div style={{ fontSize:13, color:'var(--muted-2)' }}>
            A workload gets a Service automatically when it's deployed with a port — see
            My Workloads.
          </div>
        </div>
      ) : (
        <div className="table-card">
          <table className="ds">
            <thead>
              <tr>
                {['Service', 'Workload Kind', 'Type', 'Port', 'Namespace', 'Age', ''].map(h => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(svc => {
                const isCatalog = String(svc.notes||'').startsWith('Catalog:');
                return (
                  <tr key={svc.id}>
                    <td>
                      <code style={{ fontSize:12, fontWeight:600 }}>{svc.name}</code>
                    </td>
                    <td><span className="badge">{svc.kind}</span></td>
                    <td style={{ fontSize:12, color:'var(--ink-2)' }}>
                      {svc.service_type || 'ClusterIP'}
                    </td>
                    <td style={{ fontSize:12, fontFamily:'monospace', color:'var(--ink-2)' }}>
                      {svc.port || '—'}
                    </td>
                    <td style={{ fontSize:12, color:'var(--ink-2)' }}>
                      <div>{clusterName(svc.cluster_id)}</div>
                      <div style={{ fontFamily:'monospace', fontSize:11, color:'var(--muted-2)' }}>{svc.namespace}</div>
                    </td>
                    <td style={{ fontSize:12, color:'var(--muted-2)' }}>
                      <Age ts={svc.created_at} />
                    </td>
                    <td>
                      <ActionsMenu>
                        <button style={MENU_ITEM}
                          onClick={() => navigate(isCatalog ? `/catalog/${svc.id}` : `/compute/workloads/${svc.id}`)}>
                          Open Workload
                        </button>
                      </ActionsMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
